import React from 'react'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import { AttackEvent, validAttackEventDetailsKeys } from './shared'
import { GraphQLRemoveTypename } from '../graphql'

export const attackEventQuery = gql`
  query AttackEvent($organizationID: ID!, $attackEventID: ID!) {
    attackEvent(
      organizationID: $organizationID
      attackEventID: $attackEventID
    ) {
      id
      sourceIP
      result
      detectionType
      ruleUuid
      details {
        path
        query
        command
        findings
        entryName
        start
        end
        commandArgs
        directory
      }
      httpRequest {
        body
        headers
        method
        parameters
        queryString
        uri
        valid
        version
      }
      application {
        name
        language
        id
      }
      server {
        name
        id
      }
      userInput {
        name
        type
        value
      }
      callLocation
      file
      method
      line
      lastCustomFrame
      stackFrames {
        type
        description
      }
      userInput {
        value
      }
    }
  }
`

export interface ChildProps {
  readonly attackEvent: AttackEvent
}

export interface AttackEventQueryProps {
  readonly organizationID: string
  readonly attackEventID: string
  readonly children: (props: ChildProps) => React.ReactNode
}

export function AttackEventQuery({
  organizationID,
  attackEventID,
  children
}: AttackEventQueryProps) {
  return (
    <Query
      query={attackEventQuery}
      variables={{ organizationID, attackEventID }}
      errorPolicy="all"
    >
      {(result) => {
        const { data, error, loading } = result
        if (loading) {
          return false
        }
        if (error) {
          throw new Error('Failed to get attack event data: ' + error.message)
        }
        return (
          <GraphQLRemoveTypename data={data}>
            {({ data }) => {
              const {
                attackEvent: {
                  id,
                  sourceIP,
                  result,
                  ruleUuid,
                  application,
                  server,
                  userInput,
                  details,
                  detectionType,
                  httpRequest,
                  callLocation,
                  file,
                  method,
                  line,
                  stackFrames,
                  lastCustomFrame
                }
              } = data
              return children({
                attackEvent: {
                  id,
                  sourceIP,
                  result,
                  ruleUuid,
                  application,
                  server,
                  userInput,
                  // Remove __typename
                  // Only add key/value pairs used in the UI
                  details: details
                    ? Object.keys(details)
                        .filter(
                          (key) => validAttackEventDetailsKeys.indexOf(key) >= 0
                        )
                        .reduce(
                          (obj, key) => ({
                            ...obj,
                            [key]: details[key]
                          }),
                          {}
                        )
                    : undefined,
                  detectionType,
                  httpRequest,
                  callLocation,
                  file,
                  method,
                  line,
                  stackFrames,
                  lastCustomFrame
                }
              })
            }}
          </GraphQLRemoveTypename>
        )
      }}
    </Query>
  )
}
